import { z } from 'zod';
import IValidationResult from '../../../../../common/interfaces/IValidationResult';
import IUserFormData from '../interfaces/IUserFormData';
import ValidationService from '../../../../../common/services/validation';
import ICheckedSystem from '../interfaces/ICheckedSystem';
import UserFormService from './user-form.service';
import AMBEV_DOMAINS from '../../../../../common/constants/ambev-domains';
import i18n from '../../../../../lib/i18n';

const requiredFieldMessage = i18n.t('RequiredFieldMessage');
const invalidInformation = i18n.t('InvalidInformation');
const invalidEmailMessage = i18n.t('UsuarioinvalidEmailMessage');
const invalidSystemsMessage = i18n.t('UsuarioinvalidSystemsMessage');

const isAmbevEmail = i18n.t('UsuarioisAmbevEmail');
const emailWithTP = i18n.t('UsuarioemailWithTP');
const emailInUse = i18n.t('UsuarioemailInUse');

const isFullNameRequired = i18n.t('UsuarioisFullNameRequired');

const defaultMessages = {
    invalid_type_error: invalidInformation,
    required_error: requiredFieldMessage,
};

const addCustomIssue = (ctx: z.RefinementCtx, message: string) => {
    ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message,
    });
};

const requiredString = () => {
    return z.string(defaultMessages).min(1, requiredFieldMessage);
};

const requiredSystems = (systems: ICheckedSystem[], ctx: any) => {
    const countChecked = systems.filter(x => x.isChecked).length;
    if (countChecked < 1) {
        addCustomIssue(ctx, invalidSystemsMessage);
    }
};

const checkIfFullName = (data: string, ctx: any) => {
    if (data.split(' ').filter(x => x.length !== 0).length <= 1) {
        addCustomIssue(ctx, isFullNameRequired);
    }
};

const checkIfIsAmbevEmail = (data: string, ctx: any) => {
    if (AMBEV_DOMAINS.some(domain => data.endsWith(domain))) {
        addCustomIssue(ctx, isAmbevEmail);
    }
};

const CarrierValidationSchema = () =>
    z.object({
        carrierId: requiredString(),
    });

const GeneralValidationSchema = z.object({
    name: requiredString().superRefine((data: any, ctx: any) =>
        checkIfFullName(data, ctx),
    ),
    email: requiredString()
        .email(invalidEmailMessage)
        .superRefine((data: any, ctx: any) => checkIfIsAmbevEmail(data, ctx)),
    userCarrier: CarrierValidationSchema(),
    systems: z
        .object({ isChecked: z.boolean() })
        .array()
        .superRefine((data: any, ctx: any) => requiredSystems(data, ctx)),
});

const UserEmailValidationSchema = z
    .object({
        isInvalid: z.boolean(),
        carrierName: z.string().nullable(),
        isAmbevEmail: z.boolean().nullable(),
    })
    .partial()
    .superRefine((data: any, ctx: any) => {
        if (data.isInvalid && data.isAmbevEmail) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['email'],
                message: isAmbevEmail,
            });
        }

        if (data.isInvalid && data.carrierName) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['email'],
                message: `${emailWithTP} ${data.carrierName}`,
            });
        }

        if (data.isInvalid && data.carrierName === null) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['email'],
                message: emailInUse,
            });
        }
    });

const validateInfos = (userData: IUserFormData): IValidationResult => {
    return ValidationService.validate(GeneralValidationSchema, userData);
};

const { getError } = ValidationService;

interface IEmailValidationResult extends IValidationResult {
    isDeactivated: boolean;
}

const validateUserEmail = async (
    email: string,
): Promise<IEmailValidationResult> => {
    const emailValidationResult =
        await UserFormService.checkIfUserEmailIsValid(email);

    const validationResult = ValidationService.validate(
        UserEmailValidationSchema,
        emailValidationResult,
    );

    return {
        isDeactivated: emailValidationResult.isDeactivated,
        ...validationResult,
    };
};

export default (() => {
    return {
        validateUserEmail,
        getError,
        validateInfos,
        UserEmailValidationSchema,
    };
})();
